import { Select, SelectItem, SelectOutlet } from "@swivl-mono/ui";
import React, { useEffect, useMemo } from "react";
import { IoIosArrowDown } from "react-icons/io";

import LocationsModel from "../../../../Models/Locations/Locations.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import MembersModel from "../../../../Models/Member/Members.Model";
import { ASSIGNEE_SCOPE_TOOLTIP, buildAssigneeOptions, getMemberDisplayName } from "../assigneeLocationScope";
import { FieldLabelWithInfo } from "./FieldLabelWithInfo";
import { TicketPropertyRow } from "./TicketPropertyRow";

type Assignee = {
  memberId: number;
  memberName?: string | null;
};

type AssigneeFieldProps = {
  assignees: Assignee[];
  /** The ticket's location — only members with access to it are offered (ENG-2882). */
  ticketLocationId: number | null;
  onChange: (memberIds: number[]) => void;
  pending?: boolean;
  locked?: boolean;
};

// Re-exported for the ticketing views that already import it from here.
export { getMemberDisplayName };

const NO_ASSIGNEE_VALUE = "__no_assignee__";

export function AssigneeField({ assignees, ticketLocationId, onChange, pending, locked }: AssigneeFieldProps) {
  // Subscribe to the store rather than snapshotting it into local state:
  // `loadMembers` resolves before its request completes, so a one-shot read
  // lands an empty list and never recovers, leaving the dropdown blank.
  const loadedMembers = MembersModel.useState((s) => s.members);
  const members = useMemo(() => loadedMembers ?? [], [loadedMembers]);
  const { groups } = LocationsModel.useState((s) => s);

  useEffect(() => {
    if (locked) return;
    void MembersModel.loadMembers();
    void LocationsModel.getGroupsAndLocations(false);
  }, [locked]);

  const currentAssigneeId = assignees.length > 0 ? assignees[0].memberId : null;

  const displayText = useMemo(() => {
    if (!currentAssigneeId) return "No assignee";
    const member = members.find((m) => m.id === currentAssigneeId);
    if (member) return getMemberDisplayName(member);
    const assignee = assignees.find((a) => a.memberId === currentAssigneeId);
    return assignee?.memberName ?? `Member #${currentAssigneeId}`;
  }, [currentAssigneeId, members, assignees]);

  // Locked mode never opens the picker, so skip this scoping entirely.
  const options: SelectItem[] = useMemo(() => {
    if (locked) return [];
    // Members the ticket's location rules out are listed disabled rather than
    // dropped (ENG-2961). The current assignee stays selectable even if
    // out-of-scope, since assignment overrides scope.
    const memberOptions = buildAssigneeOptions({
      members,
      orgId: Member.orgId,
      locationId: ticketLocationId,
      groups: groups ?? [],
      alwaysEligibleMemberIds: currentAssigneeId ? [currentAssigneeId] : []
    });
    return [{ label: "No Assignee", value: NO_ASSIGNEE_VALUE }, { type: "divider" as const }, ...memberOptions];
  }, [locked, members, currentAssigneeId, ticketLocationId, groups]);

  const handleChange = (selected: string[]) => {
    const val = selected[0];
    if (!val || val === NO_ASSIGNEE_VALUE) {
      onChange([]);
    } else {
      onChange([parseInt(val)]);
    }
  };

  if (locked) {
    return (
      <TicketPropertyRow label="Assignee" lockedAction="Updating assignees">
        {displayText}
      </TicketPropertyRow>
    );
  }

  return (
    <TicketPropertyRow label={<FieldLabelWithInfo label="Assignee" tooltip={ASSIGNEE_SCOPE_TOOLTIP} />} pending={pending}>
      <Select
        options={options}
        selectedValues={currentAssigneeId ? [String(currentAssigneeId)] : [NO_ASSIGNEE_VALUE]}
        onChange={handleChange}
        hasSearchInDropdown
        styles={{ [SelectOutlet.POPOVER]: { minWidth: 240, maxWidth: 320, width: "auto" } }}
        customTrigger={({ isOpen }) => (
          <button className={`flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-sm ${isOpen ? "bg-grey-100" : "hover:bg-grey-50"}`}>
            {displayText}
            <IoIosArrowDown className={`h-3.5 w-3.5 text-grey-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      />
    </TicketPropertyRow>
  );
}
